import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotificationStore } from '../../stores/notificationStore';
import { Notification, NotificationType, NotificationPriority } from '../../types/notification';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';

interface NotificationItemProps {
    notification: Notification;
    onClose?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
    const navigate = useNavigate();
    const { t } = useTranslation('notification');
    const { markAsRead, dismiss } = useNotificationStore();

    // Get localized title based on notification type
    const getLocalizedTitle = () => {
        switch (notification.type) {
            case NotificationType.NEW_MESSAGE:
                return t('newMessage.title', { name: notification.metadata?.senderName || '' });
            case NotificationType.NEW_FOLLOWER:
                return t('newFollower.title');
            case NotificationType.LEARNER_QUESTION:
                return t('learnerQuestion.title');
            case NotificationType.CREATOR_ANSWERED:
                return t('creatorAnswered.title');
            case NotificationType.MODE_UNLOCKED:
                if (notification.metadata?.kind === 'shadowing_pro') {
                    return t('modeUnlocked.shadowingPro.title');
                }
                return notification.title;
            default:
                return notification.title;
        }
    };

    // Get localized message based on notification type
    const getLocalizedMessage = () => {
        switch (notification.type) {
            case NotificationType.NEW_FOLLOWER:
                return t('newFollower.message', { name: notification.metadata?.followerName || '' });
            case NotificationType.LEARNER_QUESTION:
                return t('learnerQuestion.message', {
                    course: notification.metadata?.courseTitle || '',
                });
            case NotificationType.CREATOR_ANSWERED:
                return t('creatorAnswered.message');
            case NotificationType.MODE_UNLOCKED:
                if (notification.metadata?.kind === 'shadowing_pro') {
                    return t('modeUnlocked.shadowingPro.message');
                }
                return notification.message;
            default:
                return notification.message;
        }
    };

    // Get localized action label based on notification type
    const getLocalizedActionLabel = () => {
        switch (notification.type) {
            case NotificationType.NEW_MESSAGE:
                return t('newMessage.action');
            case NotificationType.NEW_FOLLOWER:
                return t('newFollower.action');
            case NotificationType.LEARNER_QUESTION:
                return t('learnerQuestion.action');
            case NotificationType.CREATOR_ANSWERED:
                return t('creatorAnswered.action');
            case NotificationType.MODE_UNLOCKED:
                if (notification.metadata?.kind === 'shadowing_pro') {
                    return t('modeUnlocked.shadowingPro.action');
                }
                return notification.actionLabel;
            default:
                return notification.actionLabel;
        }
    };

    const handleClick = async () => {
        // Mark as read if not already
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }

        // Navigate to action URL if exists
        if (notification.actionUrl) {
            onClose?.();
            navigate(notification.actionUrl);
        }
    };

    const handleDismiss = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await dismiss(notification.id);
    };

    const getIcon = () => {
        switch (notification.type) {
            case NotificationType.NEW_MESSAGE:
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                );
            case NotificationType.NEW_FOLLOWER:
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                );
            case NotificationType.COURSE_PROCESSING:
            case NotificationType.COURSE_PUBLISHED:
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                );
            case NotificationType.ACHIEVEMENT:
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                );
            default:
                return (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                );
        }
    };

    const getIconColor = () => {
        switch (notification.priority) {
            case NotificationPriority.URGENT:
                return 'text-red-500 bg-red-100';
            case NotificationPriority.HIGH:
                return 'text-orange-500 bg-orange-100';
            default:
                return 'text-indigo-500 bg-indigo-100';
        }
    };

    const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
        addSuffix: true,
        locale: getDateFnsLocale(),
    });

    return (
        <div
            onClick={handleClick}
            className={`
                flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-100 last:border-0
                ${notification.isRead ? 'bg-white' : 'bg-indigo-50/50'}
                hover:bg-gray-50
            `}
        >
            {/* Icon */}
            <div className={`flex-shrink-0 p-2 rounded-full ${getIconColor()}`}>
                {getIcon()}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <p className={`text-sm ${notification.isRead ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                    {getLocalizedTitle()}
                </p>
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                    {getLocalizedMessage()}
                </p>
                <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>

                {/* Action Button */}
                {notification.actionUrl && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick();
                        }}
                        className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                        {getLocalizedActionLabel()}
                    </button>
                )}
            </div>

            {/* Dismiss Button */}
            <button
                onClick={handleDismiss}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                aria-label="Dismiss"
            >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Unread Indicator */}
            {!notification.isRead && (
                <div className="flex-shrink-0 w-2 h-2 bg-indigo-500 rounded-full mt-2" />
            )}
        </div>
    );
};

export default NotificationItem;
